<template>
    <div data-test-view="goals-cycles-edit">
        <l-card-content>
            <!-- Headline -->
            <l-headline
                :title="$t(`goals.cycle.edit.title`)"
                :sub-title="$t(`goals.cycle.edit.subtitle`)"
            />
            <!--/Headline -->

            <!-- Form -->
            <div class="row">
                <div class="col-md-12">
                    <!-- Name -->
                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleTitle`) }}
                    </h4>
                    <l-input-text
                        ref="input-name"
                        v-model="cycle.name"
                        :label="''"
                        :placeholder="$t(`goals.cycle.create.placeholderTitle`)"
                        type="text"
                    />

                    <!-- Description -->
                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleDescription`) }}
                    </h4>
                    <l-input-text
                        ref="input-description"
                        v-model="cycle.description"
                        :label="''"
                        :placeholder="$t(`goals.cycle.create.placeholderDescription`)"
                        type="text"
                        optional
                    />

                    <!-- Participants -->
                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleParticipants`) }}
                    </h4>
                    <l-input-radio-group
                        v-model="cycle.participantOption"
                        :options="[
                            { label: $t(`goals.cycle.create.labelParticipantsAll`), value: `all` },
                            {
                                label: $t(`goals.cycle.create.labelParticipantsSelected`),
                                value: `selected`,
                            },
                        ]"
                    />

                    <div v-show="cycle.participantOption !== 'all'" class="row">
                        <div class="col-md-12">
                            <l-input-typeahead
                                :label="''"
                                :placeholder="$t(`goals.cycle.create.placeholderSelectParticipant`)"
                                :data="participants"
                                :on-select="typeaheadParticipantsOnSelect"
                                :on-input-change="typeaheadParticipantsOnInputChange"
                            >
                            </l-input-typeahead>
                            <span
                                v-for="(participant, index) in cycle.participants"
                                v-show="cycle.participants.length"
                                :key="participant._id"
                            >
                                <span
                                    class="label label-default"
                                    tabindex="0"
                                    @click="removeSelectedParticipant(index)"
                                    @keyup.enter="removeSelectedParticipant(index)"
                                >
                                    {{ participant.name }} <i class="fa fa-remove"></i>
                                </span>
                            </span>
                        </div>
                    </div>

                    <l-space :size="1" />

                    <!-- Timeline -->
                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleTimeline`) }}
                    </h4>

                    <div class="row">
                        <div class="col-md-3">
                            <h4 style="margin-top: 10px">
                                {{ $t(`goals.cycle.create.labelTimelinePlan`) }}
                            </h4>
                            <l-date-picker
                                :label="''"
                                :placeholder="$t(`goals.cycle.create.placeholderPlan`)"
                                :value="
                                    cycle.timeline.plan.date
                                        ? new Date(cycle.timeline.plan.date)
                                        : null
                                "
                                :on-date-change="
                                    date => {
                                        cycle.timeline.plan.date = date;
                                    }
                                "
                            />
                        </div>
                        <div class="col-md-9">
                            <h4 style="margin-top: 10px">
                                {{ $t(`goals.cycle.create.timeline.plan.whatWillHappen`) }}
                            </h4>
                            <ul style="margin-top: 20px">
                                <li>{{ $t(`goals.cycle.create.timeline.plan.1`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.plan.2`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.plan.3`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.plan.4`) }}</li>
                            </ul>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-3">
                            <h4 style="margin-top: 10px">
                                {{ $t(`goals.cycle.create.labelTimelineActive`) }}
                            </h4>
                            <l-date-picker
                                :label="''"
                                :placeholder="$t(`goals.cycle.create.placeholderActive`)"
                                :value="
                                    cycle.timeline.active.date
                                        ? new Date(cycle.timeline.active.date)
                                        : null
                                "
                                :on-date-change="
                                    date => {
                                        cycle.timeline.active.date = date;
                                    }
                                "
                            />
                        </div>
                        <div class="col-md-9">
                            <h4 style="margin-top: 10px;">
                                {{ $t(`goals.cycle.create.timeline.active.whatWillHappen`) }}
                            </h4>
                            <ul style="margin-top: 20px">
                                <li>{{ $t(`goals.cycle.create.timeline.active.1`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.active.2`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.active.3`) }}</li>
                            </ul>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-3">
                            <h4 style="margin-top: 10px">
                                {{ $t(`goals.cycle.create.labelTimelineEnd`) }}
                            </h4>
                            <l-date-picker
                                :label="''"
                                :placeholder="$t(`goals.cycle.create.placeholderEnd`)"
                                :value="
                                    cycle.timeline.end.date
                                        ? new Date(cycle.timeline.end.date)
                                        : null
                                "
                                :on-date-change="
                                    date => {
                                        cycle.timeline.end.date = date;
                                    }
                                "
                            />
                        </div>
                        <div class="col-md-9">
                            <h4 style="margin-top: 10px;">
                                {{ $t(`goals.cycle.create.timeline.end.whatWillHappen`) }}
                            </h4>
                            <ul style="margin-top: 20px">
                                <li>{{ $t(`goals.cycle.create.timeline.end.1`) }}</li>
                                <li>{{ $t(`goals.cycle.create.timeline.end.2`) }}</li>
                            </ul>
                        </div>
                    </div>

                    <div
                        v-show="
                            cycle.timeline.plan.date &&
                                cycle.timeline.active.date &&
                                cycle.timeline.end.date
                        "
                    >
                        <div class="hr-small"></div>
                        <p>{{ $t(`goals.cycle.create.timelinePreview`) }}</p>

                        <l-goal-cycle-timeline
                            :plan="cycle.timeline.plan.date"
                            :active="cycle.timeline.active.date"
                            :end="cycle.timeline.end.date"
                        />

                        <div class="hr-small"></div>
                    </div>

                    <l-space :size="1" />

                    <!-- Settings -->
                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleSettings`) }}
                    </h4>

                    <div class="row">
                        <div class="col-md-12">
                            <label class="color-dark">
                                <input
                                    v-model="cycle.settings.freezeGoalsWhenActive"
                                    type="checkbox"
                                />
                                <span>{{ $t(`goals.cycle.create.labelFreezeGoals`) }}</span>
                            </label>

                            <br />

                            <label class="color-dark">
                                <input
                                    v-model="cycle.settings.archiveGoalsWhenEnd"
                                    type="checkbox"
                                />
                                <span>{{ $t(`goals.cycle.create.labelArchiveGoals`) }}</span>
                            </label>
                        </div>
                    </div>

                    <l-space :size="1" />

                    <h4 class="goals-cycles-create-form-header">
                        {{ $t(`goals.cycle.create.titleTags`) }}
                    </h4>
                    <l-input-typeahead
                        :label="''"
                        :placeholder="$t(`goals.update.placeholderTag`)"
                        :data="tags"
                        :on-select="typeaheadTagsOnSelect"
                        :on-input-change="typeaheadTagsOnInputChange"
                        :show-picture="false"
                    >
                    </l-input-typeahead>
                    <span
                        v-for="(tag, index) in cycle.tags"
                        v-show="cycle.tags.length"
                        :key="tag._id"
                    >
                        <span
                            class="label label-default r-default-margin-s"
                            tabindex="0"
                            @click="removeSelectedTag(index)"
                            @keyup.enter="removeSelectedTag(index)"
                        >
                            {{ tag.name }} <i class="fa fa-remove l-default-margin-s"></i>
                        </span>
                    </span>

                    <l-space :size="1" />

                    <!-- Error -->
                    <div style="margin-top: 20px">
                        <div v-show="error">
                            <l-text-alert type="danger" :text="$t(`goals.cycle.error.generic`)" />
                        </div>
                    </div>
                    <!--/Error -->

                    <button class="btn btn-primary btn-raised btn-block" @click="submitHandler()">
                        {{ $t(`goals.cycle.create.btnSaveActivate`) }}
                    </button>
                </div>
            </div>
            <!-- /Form -->
        </l-card-content>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import deepClone from "@/utils/deep-clone";

import LCardContent from "@/components/LCardContent";
import LDatePicker from "@/components/LDatePicker";
import LGoalCycleTimeline from "@/components/LGoalCycleTimeline";
import LHeadline from "@/components/LHeadline";
import LInputRadioGroup from "@/components/LInputRadioGroup";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSpace from "@/components/LSpace";
import LTextAlert from "@/components/LTextAlert";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LCardContent,
        LDatePicker,
        LGoalCycleTimeline,
        LHeadline,
        LInputRadioGroup,
        LInputText,
        LInputTypeahead,
        LSpace,
        LTextAlert,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            error: false,
            cycle: {
                name: "",
                description: "",
                participantOption: "all",
                participants: [],
                timeline: {
                    plan: {
                        date: null,
                    },
                    active: {
                        date: null,
                    },
                    end: {
                        date: null,
                    },
                },
                settings: {
                    freezeGoalsWhenActive: false,
                    archiveGoalsWhenEnd: false,
                },
                tags: [],
            },
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            tagsSearchList: "tags/search/list",
            getCycleDetails: "goals/cycles/details",
        }),
        participants() {
            return [
                ...this.usersSearchList.map(user => ({
                    _id: user._id,
                    type: "user",
                    firstname: user.firstname,
                    lastname: user.lastname,
                    name: user.firstname + " " + user.lastname,
                    desc: user.teamRole.title,
                    picture: user.picture,
                })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                    desc: this.$t("typeahead.team"),
                    picture: teamPicture,
                })),
            ];
        },
        tags() {
            return [
                ...this.tagsSearchList.map(tag => ({
                    _id: tag._id,
                    name: tag.name,
                    desc: "",
                })),
            ];
        },
    },
    created() {
        this.cycle = this.parseCycleDetails();
    },
    methods: {
        parseCycleDetails() {
            const cycle = this.getCycleDetails(this.id);

            if (cycle.participants.users || cycle.participants.teams) {
                const participants = []
                    .concat(
                        (cycle.participants.users || []).map(function(user) {
                            return {
                                _id: user._id,
                                type: "user",
                                name: user.firstname + " " + user.lastname,
                            };
                        }),
                    )
                    .concat(
                        (cycle.participants.teams || []).map(function(team) {
                            return {
                                _id: team._id,
                                type: "team",
                                name: team.name,
                            };
                        }),
                    );

                cycle.participants = participants;
            } else {
                cycle.participants = [];
            }

            cycle.participantOption = cycle.participants.length ? "selected" : "all";

            cycle.timeline.plan.date = new Date(cycle.timeline.plan.date);
            cycle.timeline.active.date = new Date(cycle.timeline.active.date);
            cycle.timeline.end.date = new Date(cycle.timeline.end.date);

            cycle.tags = cycle?.defaults?.tags ?? [];

            return cycle;
        },

        typeaheadParticipantsOnSelect(participant) {
            if (!this.cycle.participants.find(p => p._id === participant._id)) {
                this.cycle.participants.push(participant);
            }
            this.$store.dispatch("users/search/reset");
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadParticipantsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        removeSelectedParticipant(index) {
            this.cycle.participants.splice(index, 1);
        },

        typeaheadTagsOnSelect(tag) {
            if (!this.cycle.tags.find(t => t._id === tag._id)) {
                this.cycle.tags.push(tag);
            }
        },
        typeaheadTagsOnInputChange(query) {
            this.$store.dispatch("tags/search/findByName", { query });
        },
        removeSelectedTag(index) {
            this.cycle.tags.splice(index, 1);
        },

        submitHandler() {
            const cycle = deepClone(this.cycle);

            // Participants
            cycle.participants = {};
            cycle.participants.all = this.cycle.participantOption === "all";

            if (!cycle.participants.all) {
                cycle.participants.users = this.cycle.participants
                    .filter(participant => participant.type === "user")
                    .map(participant => participant._id);

                cycle.participants.teams = this.cycle.participants
                    .filter(participant => participant.type === "team")
                    .map(participant => participant._id);
            }

            // Create
            this.$store
                .dispatch({ type: "goals/cycles/update", payload: { id: this.id, cycle } })
                .then(() => {
                    this.$router.push(`/goals/cycles/details/${this.id}`);
                })
                .catch(() => {
                    this.error = true;
                });
        },
    },
};
</script>

<style scoped>
.goals-cycles-create-form-header {
    margin-top: 0px;
    font-weight: bold;
}
</style>
